import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { openConsultancyCheckout } from "@/lib/razorpay";
import TechSupportChat from "@/components/TechSupportChat";
import { CheckCircle2, ArrowLeft, Loader2 } from "lucide-react";

export default function ConsultancyDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [svc, setSvc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/consultancy/services/${slug}`).then((r) => setSvc(r.data)).catch(() => toast.error("Service not found")); }, [slug]);

  const book = async () => {
    if (!user) { toast.error("Please login to book"); return nav("/login"); }
    setBusy(true);
    const prefill = { name: user?.name || "", email: user?.email || "", contact: user?.mobile || "" };
    const r = await openConsultancyCheckout({ slug, prefill });
    setBusy(false);
    if (r.ok) { toast.success("Consultation booked — you'll receive an email with details"); nav("/billing"); }
  };

  if (!svc) return <div className="mx-auto max-w-3xl px-4 py-16 text-center text-sarthi-muted">Loading…</div>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid={`consultancy-detail-${slug}`}>
      <button onClick={() => nav("/consultancy")} className="mb-4 inline-flex items-center gap-1 text-sm font-bold text-sarthi-muted hover:text-sarthi-primary"><ArrowLeft className="h-4 w-4" /> All services</button>
      <div className="rounded-2xl bg-sarthi-accent px-3 py-1 text-center text-xs font-bold uppercase text-sarthi-primary inline-block">{svc.category}</div>
      <h1 className="mt-3 font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">{svc.title}</h1>
      <p className="mt-3 text-lg text-sarthi-muted">{svc.short}</p>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="sarthi-card p-5" data-testid="scope-panel">
          <div className="mb-3 text-xs font-bold uppercase tracking-wider text-sarthi-muted">Scope of Work</div>
          <ul className="space-y-2">
            {(svc.scope || []).map((s, i) => (<li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" /> {s}</li>))}
          </ul>
        </div>
        <div className="sarthi-card p-5" data-testid="specs-panel">
          <div className="mb-3 text-xs font-bold uppercase tracking-wider text-sarthi-muted">Technical Specifications</div>
          <ul className="space-y-2">
            {(svc.specs || []).map((s, i) => (<li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-sarthi-primary" /> {s}</li>))}
          </ul>
        </div>
      </div>

      <div className="mt-8 sarthi-card p-6 text-center">
        <div className="font-display text-4xl font-extrabold text-sarthi-primary">₹{svc.amount}<span className="ml-1 text-base font-bold text-sarthi-muted"> / session</span></div>
        <button data-testid="book-consultant-btn" onClick={book} disabled={busy} className="sarthi-btn-primary mt-4 inline-flex items-center gap-2 px-8 py-3 text-white disabled:opacity-50">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening checkout…</> : "Book Consultant"}
        </button>
        <div className="mt-3 text-xs text-sarthi-muted">Paid via Razorpay · UPI / Cards / Netbanking · Invoice emailed instantly</div>
      </div>

      <TechSupportChat />
    </div>
  );
}
